import { useCallback, useState, useEffect } from 'react';
import qs from 'query-string';

export const setQueryStringWithoutPageReload = (qsValue?: string) => {
  if (typeof window !== `undefined`) {
    const newurl =
      window.location.protocol +
      '//' +
      window.location.host +
      window.location.pathname +
      qsValue;
    window.history.pushState({ path: newurl }, '', newurl);
  }
};

export const setQueryValue = (key: string, value?: string) => {
  if (typeof window !== `undefined`) {
    const values = qs.parse(window.location.search);
    const newQsValue = qs.stringify({ ...values, [key]: value });
    setQueryStringWithoutPageReload(`?${newQsValue}`);
  }
};

export const getQueryStringValue = (key: string, initialValue?: string) => {
  if (typeof window !== `undefined`) {
    const values = qs.parse(window.location.search);
    return (values[key] as string) || initialValue;
  } else {
    return initialValue;
  }
};

export const useQueryString = (
  key: string,
  initialValue?: string
): [string, (value: string) => void] => {
  const [value, setValue] = useState(getQueryStringValue(key, initialValue));
  const onSetValue = useCallback(
    (newValue: string) => {
      setValue(newValue);
      setQueryValue(key, newValue);
    },
    [key]
  );

  return [value, onSetValue];
};

export const getQueryBoolValue = (key: string, initialValue?: boolean): boolean => {
  let value = initialValue;
  const qsValue = getQueryStringValue(key);
  if (qsValue) {
    value = qsValue === 'true';
  }
  return value;
};

export const useQueryBool = (
  key: string,
  initialValue?: boolean
): [boolean, (value: boolean) => void] => {
  const [value, setValue] = useState<boolean>(initialValue ?? false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  useEffect(() => {
    const initVal = getQueryBoolValue(key, initialValue);
    setValue(initVal);
    setIsLoaded(true);
  }, [key, initialValue]);

  const onSetValue = useCallback(
    (newValue: boolean) => {
      setValue(newValue);
      setQueryValue(key, String(newValue));
    },
    [key]
  );

  return [isLoaded ? value : initialValue ?? false, onSetValue];
};

export const getQueryNumberValue = (key: string, initialValue?: number) => {
  let value = initialValue;
  const qsValue = getQueryStringValue(key);
  if (qsValue) {
    value = parseInt(qsValue, 10);
  }
  return value;
};

export const useQueryNumber = (
  key: string,
  initialValue?: number
): [number, (value: number) => void] => {
  const initVal = getQueryNumberValue(key, initialValue);

  const [value, setValue] = useState<number>(initVal);
  const onSetValue = useCallback(
    (newValue: number) => {
      setValue(newValue);
      setQueryValue(key, String(newValue));
    },
    [key]
  );

  return [value, onSetValue];
};
